import { CommonModule } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { MatTabGroup, MatTabsModule } from '@angular/material/tabs';

import { VOneTab } from '@shared-models';

// Design System mobile range (sm: ≤ 575px, see styles/utils/_breakpoints.scss).
const SWIPE_MEDIA_QUERY = '(max-width: 575px)';
// Movement (px) before a touch is classified as a horizontal or vertical drag.
const SWIPE_AXIS_THRESHOLD = 6;

interface SwipeState {
  startX: number;
  startY: number;
  startDistance: number;
  horizontal: boolean | null;
}

@Component({
  selector: 'v-one-tabs',
  imports: [CommonModule, MatTabsModule],
  templateUrl: './v-one-tabs.component.html',
  styleUrls: ['./v-one-tabs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VOneTabsComponent {
  // Inputs
  tabs = input.required<VOneTab[]>();
  activeTabId = input<string>('');
  disabled = input<boolean>(false);
  className = input<string>('');
  dynamicHeight = input<boolean>(false);
  // Mobile only: drag the tab header sideways, besides the pagination arrows.
  swipeable = input<boolean>(false);

  // Outputs
  tabChange = output<string>();
  tabClick = output<{ tabId: string; tab: VOneTab }>();

  // Internal state
  private selectedIndexSignal = signal<number>(0);
  private readonly tabGroup = viewChild(MatTabGroup);
  private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private swipe: SwipeState | null = null;

  // Effects
  constructor() {
    afterNextRender(() => this.listenForSwipe());

    effect(() => {
      const externalActiveId = this.activeTabId();
      const tabs = this.tabs();
      if (externalActiveId && tabs.length > 0) {
        const index = tabs.findIndex((tab) => tab.id === externalActiveId);
        if (index !== -1 && index !== untracked(this.selectedIndexSignal)) {
          this.selectedIndexSignal.set(index);
        }
      }
    });

    // Initialize with first enabled tab if no active tab is set
    effect(() => {
      const tabs = this.tabs();
      const currentIndex = untracked(this.selectedIndexSignal);

      if (tabs.length > 0 && currentIndex === 0 && !this.activeTabId()) {
        const firstEnabledIndex = tabs.findIndex((tab) => !tab.disabled);
        if (firstEnabledIndex !== -1) {
          this.selectedIndexSignal.set(firstEnabledIndex);
        }
      }
    });
  }

  // Computed properties
  readonly tabsClasses = computed(() => {
    const baseClass = 'v-one-tabs';
    const disabledClass = this.disabled() ? `${baseClass}--disabled` : '';
    const swipeableClass = this.swipeable() ? `${baseClass}--swipeable` : '';
    const customClass = this.className();

    return [baseClass, disabledClass, swipeableClass, customClass]
      .filter(Boolean)
      .join(' ');
  });

  readonly selectedIndex = computed(() => this.selectedIndexSignal());

  // Methods
  onTabChanged(event: { index: number }): void {
    const selectedIndex = event.index;
    const selectedTab = this.tabs()[selectedIndex];

    if (selectedTab && !selectedTab.disabled && !this.disabled()) {
      this.selectedIndexSignal.set(selectedIndex);
      this.tabChange.emit(selectedTab.id);
      this.tabClick.emit({ tabId: selectedTab.id, tab: selectedTab });
    }
  }

  isTabDisabled(tab: VOneTab): boolean {
    return tab.disabled || this.disabled();
  }

  private listenForSwipe(): void {
    const host = this.hostRef.nativeElement;
    const options: AddEventListenerOptions = { passive: true };
    const onStart = (event: TouchEvent) => this.onSwipeStart(event);
    const onMove = (event: TouchEvent) => this.onSwipeMove(event);
    const onEnd = () => (this.swipe = null);

    host.addEventListener('touchstart', onStart, options);
    host.addEventListener('touchmove', onMove, options);
    host.addEventListener('touchend', onEnd, options);
    host.addEventListener('touchcancel', onEnd, options);

    this.destroyRef.onDestroy(() => {
      host.removeEventListener('touchstart', onStart);
      host.removeEventListener('touchmove', onMove);
      host.removeEventListener('touchend', onEnd);
      host.removeEventListener('touchcancel', onEnd);
    });
  }

  private onSwipeStart(event: TouchEvent): void {
    this.swipe = null;
    const header = this.tabGroup()?._tabHeader;
    const target = event.target as Element | null;

    if (
      !this.swipeable() ||
      !header ||
      event.touches.length !== 1 ||
      !target?.closest('.mat-mdc-tab-header') ||
      !window.matchMedia(SWIPE_MEDIA_QUERY).matches
    ) {
      return;
    }

    const touch = event.touches[0];
    this.swipe = {
      startX: touch.clientX,
      startY: touch.clientY,
      startDistance: header.scrollDistance,
      horizontal: null,
    };
  }

  private onSwipeMove(event: TouchEvent): void {
    const header = this.tabGroup()?._tabHeader;
    if (!this.swipe || !header) return;

    const touch = event.touches[0];
    const deltaX = touch.clientX - this.swipe.startX;
    const deltaY = touch.clientY - this.swipe.startY;

    if (this.swipe.horizontal === null) {
      if (
        Math.abs(deltaX) < SWIPE_AXIS_THRESHOLD &&
        Math.abs(deltaY) < SWIPE_AXIS_THRESHOLD
      ) {
        return;
      }
      this.swipe.horizontal = Math.abs(deltaX) > Math.abs(deltaY);
    }

    if (!this.swipe.horizontal) {
      this.swipe = null;
      return;
    }

    header.scrollDistance = this.swipe.startDistance - deltaX;
  }
}
